// ./src/components/generic/RoomSelector/RoomSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select, MultiSelect, MultiSelectProps } from "@mantine/core";
import { useRooms } from "@/hooks/useRooms";
import { LoadingIndicator } from "@/components/generic";
import { Room } from "@/utils/api";
import { useApp } from "@/contexts";

export interface RoomSelectorProps {
  /** Selected room ID(s) - string for single select, string[] for multiple */
  value: string | string[] | null | undefined;

  /** Callback when room selection changes */
  onChange?: (roomId: string | string[] | null, room?: Room | Room[]) => void;

  /** Venue ID to filter rooms by */
  venueId: string | null;

  /** Whether to allow multiple selection */
  multiple?: boolean;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive rooms */
  includeInactive?: boolean;

  /** Custom filter function for rooms */
  roomFilter?: (room: Room) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select/MultiSelect */
  [key: string]: any;
}

export const RoomSelector: React.FC<RoomSelectorProps> = ({
  value,
  onChange,
  venueId,
  multiple = false,
  placeholder = "Select a room",
  includeInactive = false,
  roomFilter,
  required = false,
  loadingPlaceholder = "Loading rooms...",
  emptyText = "No rooms available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { user } = useApp();
  // Use rooms hook with venue filter
  const { rooms, loading, isEmpty, lastError } = useRooms({
    venueId,
    autoLoad: true,
    userId: user?.id,
  });

  // Filter and prepare room options
  const roomOptions = useMemo(() => {
    let filteredRooms = rooms;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredRooms = filteredRooms.filter(
        (room) => room.status === 100 // Only show active rooms
      );
    }

    // Apply custom filter if provided
    if (roomFilter) {
      filteredRooms = filteredRooms.filter(roomFilter);
    }

    // Sort by name for better UX
    filteredRooms.sort((a, b) => {
      const nameA = a.name || "Unnamed Room";
      const nameB = b.name || "Unnamed Room";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredRooms.map((room) => ({
      value: room.id || "",
      label: room.name || "Unnamed Room",
      disabled: room.status === 0, // Disabled rooms have status 0
    }));
  }, [rooms, includeInactive, roomFilter]);

  // Create a map for quick room lookup by ID
  const roomMap = useMemo(() => {
    const map = new Map();
    rooms.forEach((room) => {
      if (room.id) {
        map.set(room.id, room);
      }
    });
    return map;
  }, [rooms]);

  // Auto-select first room if required and no value provided
  useEffect(() => {
    if (required && roomOptions.length > 0 && !loading && onChange) {
      // For single select mode
      if (!multiple && !value) {
        const firstRoom = roomOptions[0];
        if (firstRoom && !firstRoom.disabled) {
          const roomObject = roomMap.get(firstRoom.value);
          onChange(firstRoom.value, roomObject);
        }
      }
      // For multiple select mode
      else if (
        multiple &&
        (!value || (Array.isArray(value) && value.length === 0))
      ) {
        const firstRoom = roomOptions[0];
        if (firstRoom && !firstRoom.disabled) {
          const roomObject = roomMap.get(firstRoom.value);
          onChange([firstRoom.value], [roomObject]);
        }
      }
    }
  }, [required, value, roomOptions, loading, onChange, roomMap, multiple]);

  // Handle onChange with room object lookup
  const handleChange = (selectedValue: string | string[] | null) => {
    if (!multiple) {
      // Single select mode
      const selectedRoomId = selectedValue as string | null;
      if (selectedRoomId) {
        const selectedRoom = roomMap.get(selectedRoomId);
        onChange?.(selectedRoomId, selectedRoom);
      } else {
        // When clearing selection, pass null for both ID and room
        onChange?.(null, undefined);
      }
    } else {
      // Multiple select mode
      const selectedRoomIds = selectedValue as string[] | null;
      if (selectedRoomIds && selectedRoomIds.length > 0) {
        const selectedRooms = selectedRoomIds
          .map((id) => roomMap.get(id))
          .filter(Boolean); // Remove any undefined values
        onChange?.(selectedRoomIds, selectedRooms);
      } else {
        // When clearing selection, pass empty arrays
        onChange?.([], []);
      }
    }
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    // Loading state
    if (loading && rooms.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={size} color="gray" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading rooms",
        disabled: true,
        error: error || "Failed to load rooms",
      };
    }

    // Empty state
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    // Normal state with data
    return {
      data: roomOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && rooms.length > 0 ? (
          <LoadingIndicator size={size} color="gray" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  // Normalize value for the select component
  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value ?? null;

  const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
    option,
  }) => {
    return option.label;
  };

  // Render the appropriate select component
  if (multiple) {
    return (
      <MultiSelect
        {...selectProps}
        size={size}
        data={selectState.data}
        renderOption={renderMultiSelectOption}
        value={normalizedValue as string[]}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required}
        searchable
        required={required}
        disabled={selectState.disabled}
        error={selectState.error || error}
        rightSection={selectState.rightSection}
      />
    );
  }

  // Render single select
  return (
    <Select
      {...selectProps}
      size={size}
      data={selectState.data}
      value={normalizedValue as string | null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error || error}
      rightSection={selectState.rightSection}
    />
  );
};
